import type {
  DropdownChangeFn,
  DropdownFilterCallback,
} from '../../../components/Dropdown/Dropdown';
import type { DropdownOption } from '../../../components/Dropdown/DropdownOption/DropdownOption';
import type { ContentTypeDefinition } from '../../../lib/flotiq-client/types/api-response';
import type { UseAppForm } from '../../form';
import type { FieldPermissionsRule } from '../FieldPermissions/FieldPermisssions';

import { useStore } from '@tanstack/react-form';
import { useCallback } from 'react';

import { EMPTY_ARRAY } from '../../../lib/globals';
import { deepReadKeyValue } from '../../../lib/helpers';

import Dropdown from '../../../components/Dropdown/Dropdown';

import { useFieldContext, useFormContext } from '../../form';

export type PermissionContentTypeDefinition = {
  id: string;
  metaDefinition: ContentTypeDefinition['metaDefinition'];
  required: string[];
};

interface PermissionContentTypeProps {
  /**
   * Name of main field for the permission, e.g. permission[0]
   */
  permissionFieldName: string;
  /**
   * Permission available rules
   */
  ruleColumn?: FieldPermissionsRule[];
  /**
   * Content type options
   */
  ctdsOptions?: DropdownOption[];
  /**
   * If field is disabled
   */
  disabled?: boolean;
  /**
   * Content types dropdown placeholder
   */
  placeholder?: string;
  /**
   * Callback to filter content types
   */
  ctdsFilterCallback?: DropdownFilterCallback;
  /**
   * Test id for field
   */
  testId?: string;
}

const PermissionContentType = ({
  permissionFieldName,
  ctdsOptions = EMPTY_ARRAY,
  ruleColumn = EMPTY_ARRAY,
  disabled = false,
  placeholder = '',
  ctdsFilterCallback,
  testId = '',
}: PermissionContentTypeProps) => {
  const form = useFormContext() as unknown as UseAppForm;
  const field = useFieldContext<PermissionContentTypeDefinition>();

  const permissionType = useStore(
    form.store,
    (state) =>
      deepReadKeyValue(`${permissionFieldName}.type`, state.values) as string,
  );

  const updateAttributePermissions = useCallback(
    (newOrder: string[], oldOrder: string[]) => {
      if (!newOrder) {
        form.setFieldValue(
          `${permissionFieldName}.attributesPermission`,
          undefined,
        );
        return;
      }

      ruleColumn.forEach(({ name, isFieldPermission }) => {
        if (!isFieldPermission) return;
        if (!oldOrder && newOrder) {
          form.setFieldValue(`${permissionFieldName}.${name}`, false);
        } else {
          form.setFieldValue(
            `${permissionFieldName}.attributesPermission.${name}`,
            (prev?: string[]) => {
              if (!prev) return;
              if (prev?.length === oldOrder?.length) return newOrder;
              return prev.filter((property) => newOrder.includes(property));
            },
          );
        }
      });
    },
    [form, permissionFieldName, ruleColumn],
  );

  const onChange = useCallback<DropdownChangeFn>(
    (e) => {
      if (permissionType === 'CO') {
        const newOrder = e.option?.metaDefinition?.order;
        const oldOrder = field.state.value?.metaDefinition?.order;
        updateAttributePermissions(newOrder, oldOrder);
      }

      field.handleChange({
        id: e.target.value,
        metaDefinition: e.option?.metaDefinition,
        required: e.option?.schemaDefinition?.required,
      });
    },
    [field, permissionType, updateAttributePermissions],
  );

  return (
    <Dropdown
      name={field.name}
      value={field.state.value?.id}
      onChange={onChange}
      onBlur={field.handleBlur}
      options={ctdsOptions}
      debounceTime={150}
      placeholder={placeholder}
      disabled={disabled}
      filterCallback={ctdsFilterCallback}
      additionalClasses="min-w-0 grow"
      nullable
      testId={testId}
    />
  );
};

export default PermissionContentType;
